import React, { useState } from "react";
import { useRouter } from "expo-router";
import { PencilIcon } from "lucide-react-native";
import { toast } from "sonner-native";

import { EligibilityScreenLayout } from "@/blocks/EligibilityScreenLayout";
import { MemberAccordionSelectInput } from "@/blocks/MemberAccordionSelectInput";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnForm } from "@/components/VnForm";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateHouseholdMemberMutation } from "@/data/mutations/householdMembersMutations";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import {
  SPECIAL_CONDITIONS_MAP,
  SpecialConditionsEnum,
} from "@/domain/eligibility";
import {
  HouseholdMemberType,
  HouseholdMemberUpdateType,
} from "@/domain/householdMember";
import { useSpecialConditionsForm } from "@/hooks/useMemberConditionsAndStatusForm";
import { useTranslator } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { dateAsDayjs } from "@/utils/date";
import { getSpecialConditionsFieldLabel } from "@/utils/eligibility";

import { PregnancyDetails } from "./PregnancyDetails";

export const EligibilitySpecialConditionsScreen = () => {
  const { t } = useTranslator();
  const { form, householdMembers, setNestedValue, watchNestedValue } =
    useSpecialConditionsForm();
  const [isNoneOfTheAboveSelected, setIsNoneOfTheAboveSelected] =
    useState(false);

  const router = useRouter();

  // Check if any member has an active special condition loaded from database
  const hasExistingData = () => {
    if (!householdMembers) {
      return false;
    }

    return householdMembers.some(member => {
      return Object.values(SpecialConditionsEnum).some(status => {
        const memberValue = member[status as keyof HouseholdMemberType];
        const activeValue = SPECIAL_CONDITIONS_MAP[status]?.active;
        return memberValue === activeValue;
      });
    });
  };

  const [isPregnancyDetailOpen, setIsPregnancyDetailOpen] = useState(false);
  const [selectedMember, setSelectedMember] =
    useState<HouseholdMemberType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: updateHouseholdMember } =
    useUpdateHouseholdMemberMutation();
  const { mutate: updateHousehold } = useUpdateHouseholdMutation();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();

  const handleOpenPregnancyDetail = (member: HouseholdMemberType) => {
    setSelectedMember(member);
    setIsPregnancyDetailOpen(true);
  };

  const handleSavePregnancyDetails = ({
    memberId,
    babiesExpected,
    dueDate,
  }: {
    memberId: string;
    babiesExpected: number;
    dueDate: string | null;
  }) => {
    setNestedValue({
      memberId,
      field: "pregnantBabiesExpected",
      value: String(babiesExpected),
    });
    setNestedValue({ memberId, field: "pregnantDueDate", value: dueDate });
  };

  if (!currentHouseholdMember?.householdId) {
    return <VnLoadingSpinner className="mt-2" />;
  }

  const handleNextPress = async () => {
    if (!householdMembers) {
      return;
    }

    setIsLoading(true);

    const formValues = form.getValues();

    const updatePromises = householdMembers.map(member => {
      const memberFormData = formValues[member.id];
      if (!memberFormData) {
        return Promise.resolve();
      }

      const updateData = {
        pregnantStatus: memberFormData.pregnantStatus,
        breastCancerStatus: memberFormData.breastCancerStatus,
        cervicalCancerStatus: memberFormData.cervicalCancerStatus,
        blindOrVisionLossStatus: memberFormData.blindOrVisionLossStatus,
        disabledStatus: memberFormData.disabledStatus,
        fosterCareFormerlyStatus: memberFormData.fosterCareFormerlyStatus,
        pregnantBabiesExpected: memberFormData.pregnantBabiesExpected
          ? Number(memberFormData.pregnantBabiesExpected)
          : null,
        pregnantDueDate: memberFormData.pregnantDueDate,
      } as HouseholdMemberUpdateType;

      return updateHouseholdMember({
        id: member.id,
        householdMemberData: updateData,
      });
    });

    const results = await Promise.allSettled(updatePromises);

    const failedCount = results.filter(
      result => result.status === "rejected",
    ).length;
    const hasAllFailed = failedCount === results.length;

    if (hasAllFailed) {
      toast.error(
        t("native:eligibilitySpecialConditionsScreen.errors.allUpdatesFailed"),
      );
      setIsLoading(false);
      return;
    }

    if (failedCount > 0) {
      toast.error(t("native:someHouseholdMembersNotUpdated"));
    }

    setIsLoading(false);

    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: "FINISHED",
        },
      },
      {
        onSuccess: () => {
          router.navigate("/eligibility/results");
        },
      },
    );
  };

  const getPregnancyData = (memberId: string) => {
    const babiesExpected = watchNestedValue({
      memberId,
      field: "pregnantBabiesExpected",
    });
    const pregnantDueDate = watchNestedValue({
      memberId,
      field: "pregnantDueDate",
    });
    const hasPregnancyData =
      Boolean(babiesExpected) && Boolean(pregnantDueDate);
    return { babiesExpected, pregnantDueDate, hasPregnancyData };
  };

  return (
    <EligibilityScreenLayout>
      <VnBox className="gap-2">
        <VnText
          variant="headerTwo"
          i18nKey="native:eligibilitySpecialConditionsScreen.title"
        />
        <VnText
          variant="lg"
          className="text-muted-foreground"
          i18nKey="native:eligibilitySpecialConditionsScreen.description"
        />
      </VnBox>

      <VnForm form={form} isInputOnlyForm>
        {householdMembers && (
          <MemberAccordionSelectInput
            options={Object.values(SpecialConditionsEnum).map(status => ({
              optionName: status,
              optionValue: SPECIAL_CONDITIONS_MAP[status],
              i18nKey: getSpecialConditionsFieldLabel(status),
            }))}
            members={householdMembers}
            renderMemberInfo={({ optionName, member }) => {
              if (optionName !== SpecialConditionsEnum.PREGNANT_STATUS) {
                return null;
              }
              const { babiesExpected, pregnantDueDate, hasPregnancyData } =
                getPregnancyData(member.id);
              if (!hasPregnancyData) {
                return null;
              }
              return (
                <VnText
                  variant="md"
                  className="text-muted-foreground"
                  i18nKey={
                    "native:eligibilitySpecialConditionsScreen.pregnancyInfo.babiesWithCount" as I18nKeyType
                  }
                  i18nOptions={{
                    count: Number(babiesExpected),
                    date: pregnantDueDate
                      ? dateAsDayjs(pregnantDueDate).format("MM/DD/YYYY")
                      : "",
                  }}
                />
              );
            }}
            onNoneOfTheAboveSelected={setIsNoneOfTheAboveSelected}
            renderMemberActions={({ optionName, member }) => {
              if (optionName !== SpecialConditionsEnum.PREGNANT_STATUS) {
                return null;
              }
              const { hasPregnancyData } = getPregnancyData(member.id);
              return (
                <VnButton
                  size={hasPregnancyData ? "icon" : "sm"}
                  variant={hasPregnancyData ? "ghost" : "default"}
                  IconComponent={hasPregnancyData ? PencilIcon : undefined}
                  onPress={() => handleOpenPregnancyDetail(member)}>
                  {!hasPregnancyData && (
                    <VnText i18nKey="native:eligibilitySpecialConditionsScreen.addDetails" />
                  )}
                </VnButton>
              );
            }}
          />
        )}

        <PregnancyDetails
          open={isPregnancyDetailOpen}
          onOpenChange={setIsPregnancyDetailOpen}
          member={selectedMember}
          onSave={(babiesExpected, dueDate) => {
            if (selectedMember) {
              handleSavePregnancyDetails({
                memberId: selectedMember.id,
                babiesExpected,
                dueDate,
              });
            }
          }}
        />
      </VnForm>
      <VnButton
        onPress={() => {
          void handleNextPress();
        }}
        disabled={
          !form.formState.isDirty &&
          !isNoneOfTheAboveSelected &&
          !hasExistingData()
        }
        isLoading={isLoading}
        i18nKey="common:next"
      />
    </EligibilityScreenLayout>
  );
};
